import { ISearchElementStyle } from './searchPageConfig';

// Admin-editor mirror of the shopper product-page config
// (ac-storefront-web: Components/Helpers/productPageConfig.ts). Persisted as a single JSON string
// on IStorefrontSettings.productPageConfig. Each section is a bag of CSS props (ISearchElementStyle)
// applied to a bounding component of the product page. Absent config = zero inline styles (strictly additive).
export interface IProductPageConfig {
    container?: ISearchElementStyle;
    title?: ISearchElementStyle;
    image?: ISearchElementStyle;
    // The frame directly around the main image (inside the gallery wrapper).
    imageFrame?: ISearchElementStyle;
    // The scrollable thumbnail strip / carousel; set scrollbarColor/scrollbarWidth here to
    // style its scrollbar when it overflows.
    galleryStrip?: ISearchElementStyle;
    // An individual thumbnail button within the carousel.
    thumb?: ISearchElementStyle;
    // The currently-selected thumbnail. Merged OVER `thumb`, so it only needs the properties that
    // mark it as selected. Left unset, the shopper app applies a default selection ring.
    thumbSelected?: ISearchElementStyle;
    // The gallery's left/right chevron buttons (shown only when the thumbnail strip overflows).
    scrollButton?: ISearchElementStyle;
    // The product tag chip row (only rendered when the storefront enables product tags).
    tags?: ISearchElementStyle;
    // The facet attribute chip row.
    attributes?: ISearchElementStyle;
    priceBlock?: ISearchElementStyle;
    msrp?: ISearchElementStyle;

    // --- Promotions ---
    // Only rendered when a promo actually priced the product, so a storefront running none never sees
    // any of these. They stack under the price, above the list price.
    // The promo name pill.
    promoBadge?: ISearchElementStyle;
    // The "Original Price: $X" line, whose amount is struck through.
    promoOriginalPrice?: ISearchElementStyle;
    // The "Ends <date>" line. Only rendered when the winning promo has an end date.
    promoEndDate?: ISearchElementStyle;

    quantity?: ISearchElementStyle;
    addToCartButton?: ISearchElementStyle;
    // The "Add to Cart" text label beside the button (defaults to a white pill with green text).
    addToCartLabel?: ISearchElementStyle;
    description?: ISearchElementStyle;
    // Behavior flag (not a style section): clamp the description with a "Show more" toggle.
    descriptionCollapsible?: boolean;
    // Collapsed height in px used when descriptionCollapsible is true (default 160).
    descriptionCollapsedHeight?: number;
    // The whole linked-products area below the description (only rendered when the product has
    // curated linked items and the Show Linked Items cascade allows them).
    linkedSection?: ISearchElementStyle;
    // The per-type group heading (h2, e.g. "Accessories").
    linkedHeading?: ISearchElementStyle;
    // The horizontal scroll row of cards; set scrollbarColor/scrollbarWidth here to style its
    // scrollbar when it overflows.
    linkedRow?: ISearchElementStyle;
    // An individual linked-item card wrapper.
    linkedCard?: ISearchElementStyle;
    // The "Recommended" badge on recommended items.
    linkedRecommendedBadge?: ISearchElementStyle;
    // Behavior flag (not a style section): show the add-to-cart control on linked-item cards
    // (default true when absent).
    linkedShowAddToCart?: boolean;

    // "Frequently Added Together" row (market-basket product-page recommendations). Styled
    // independently from linked items. Only rendered live when enableFrequentlyAddedTogether is on.
    // The whole section container (background/padding/border around the heading + slider).
    addedTogetherSection?: ISearchElementStyle;
    // The "Frequently Added Together" heading (h2).
    addedTogetherHeading?: ISearchElementStyle;
    // An individual product card in the slider.
    addedTogetherCard?: ISearchElementStyle;
    // The slider's left/right chevron (scroll) buttons.
    addedTogetherScrollButton?: ISearchElementStyle;
    // The horizontal scrolling row; set scrollbarColor/scrollbarWidth here to style its scrollbar.
    addedTogetherScrollbar?: ISearchElementStyle;

    // "More Like This" row (similar-products recommendations). Styled independently from linked
    // items. Only rendered live when enableMoreLikeThis is on.
    // The whole section container (background/padding/border around the heading + slider).
    moreLikeThisSection?: ISearchElementStyle;
    // The "More Like This" heading (h2).
    moreLikeThisHeading?: ISearchElementStyle;
    // An individual product card in the slider.
    moreLikeThisCard?: ISearchElementStyle;
    // The slider's left/right chevron (scroll) buttons.
    moreLikeThisScrollButton?: ISearchElementStyle;
    // The horizontal scrolling row; set scrollbarColor/scrollbarWidth here to style its scrollbar.
    moreLikeThisScrollbar?: ISearchElementStyle;
}

export const PRODUCT_PAGE_SECTIONS = [
    'container', 'title', 'image', 'imageFrame', 'galleryStrip', 'thumb', 'thumbSelected',
    'scrollButton', 'tags', 'attributes', 'priceBlock', 'msrp', 'quantity', 'addToCartButton',
    'addToCartLabel', 'description',
    'promoBadge', 'promoOriginalPrice', 'promoEndDate',
    'linkedSection', 'linkedHeading', 'linkedRow', 'linkedCard', 'linkedRecommendedBadge',
    'addedTogetherSection', 'addedTogetherHeading', 'addedTogetherCard', 'addedTogetherScrollButton', 'addedTogetherScrollbar',
    'moreLikeThisSection', 'moreLikeThisHeading', 'moreLikeThisCard', 'moreLikeThisScrollButton', 'moreLikeThisScrollbar',
] as const;

export const DEFAULT_PRODUCT_PAGE_CONFIG: IProductPageConfig = {};

export function parseProductPageConfig(raw?: string): IProductPageConfig {
    if (!raw) return { ...DEFAULT_PRODUCT_PAGE_CONFIG };
    try {
        return { ...DEFAULT_PRODUCT_PAGE_CONFIG, ...(JSON.parse(raw) as IProductPageConfig) };
    } catch {
        return { ...DEFAULT_PRODUCT_PAGE_CONFIG };
    }
}

export function serializeProductPageConfig(config: IProductPageConfig): string {
    return JSON.stringify(config);
}
